import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { RefreshCw, ChevronDown, Image as ImageIcon, Zap } from 'lucide-react'

const PROVIDER_ICONS: Record<string, string> = {
    anthropic: '/images/foreground/icons/vsiconclaude.png',
    openai: '/images/foreground/icons/vsiconchatgpt.png',
    google: '/images/foreground/icons/vsicongemini.png',
}
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'
import { useAuthStore } from '@/app/store/auth'
import api from '@/lib/api/axios'

type SeverityFilter = 'all' | 'critical' | 'warning' | 'info'

/** Minimal shape of the i18next `t` used by the module-level helpers below. */
type Translate = (key: string, fallback: string, options?: Record<string, unknown>) => string

interface ChangeEvent {
    id: number
    changeType: string
    severity: string
    title: string
    description: string
    previousValue: string | null
    newValue: string | null
    affectedScenarios: string | null
    requiresAction: boolean
    modelId: string
    createdAt: string
    provider?: { name: string; displayName: string }
}

function severityDotColor(severity: string): string {
    switch (severity) {
        case 'critical': return 'bg-red-500'
        case 'warning': return 'bg-yellow-500'
        case 'info': return 'bg-green-500'
        default: return 'bg-gray-400'
    }
}

// Pastel pill styling per change type: soft fill + darker text + soft border,
// with muted tints in dark mode.
function changeTypeBadge(changeType: string, t: Translate): { label: string; className: string } {
    switch (changeType) {
        case 'new_model':
            return { label: t('aiModels.changeType.new', 'NEW'), className: 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-500/15 dark:text-blue-300 dark:border-blue-500/30' }
        case 'model_removed':
            return { label: t('aiModels.changeType.removed', 'REMOVED'), className: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-500/15 dark:text-red-300 dark:border-red-500/30' }
        case 'model_updated':
            return { label: t('aiModels.changeType.updated', 'UPDATED'), className: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-500/30' }
        case 'model_restored':
            return { label: t('aiModels.changeType.restored', 'RESTORED'), className: 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-500/30' }
        case 'model_auto_assigned':
            return { label: t('aiModels.changeType.autoAssigned', 'AUTO-ASSIGNED'), className: 'bg-violet-100 text-violet-700 border-violet-200 dark:bg-violet-500/15 dark:text-violet-300 dark:border-violet-500/30' }
        // A newer model sync would have assigned, held back because a test request to it failed.
        case 'model_not_assigned':
            return { label: t('aiModels.changeType.notAssigned', 'NOT ASSIGNED'), className: 'bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-500/15 dark:text-orange-300 dark:border-orange-500/30' }
        default:
            return { label: changeType.toUpperCase(), className: 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-500/15 dark:text-gray-300 dark:border-gray-500/30' }
    }
}

function timeAgo(dateStr: string, t: Translate): string {
    const now = Date.now()
    const then = new Date(dateStr).getTime()
    const mins = Math.floor((now - then) / 60000)
    if (mins < 60) return t('aiModels.time.minutesAgo', '{{minutes}}m ago', { minutes: mins })
    const hours = Math.floor(mins / 60)
    if (hours < 24) return t('aiModels.time.hoursAgo', '{{hours}}h ago', { hours })
    const days = Math.floor(hours / 24)
    return t('aiModels.time.daysAgo', '{{days}}d ago', { days })
}

export function ModelChangesTab() {
    const { t } = useTranslation()
    const { toast } = useToast()
    const queryClient = useQueryClient()
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

    const [severityFilter, setSeverityFilter] = useState<SeverityFilter>('all')
    const [requiresActionOnly, setRequiresActionOnly] = useState(false)
    const [expandedId, setExpandedId] = useState<number | null>(null)

    const { data: statsData } = useQuery({
        queryKey: ['ai-changes-stats'],
        queryFn: async () => {
            const res = await api.get('/api/ai/models/changes/stats')
            return res.data?.stats
        },
        enabled: isAuthenticated,
    })

    const { data: changesData, isLoading } = useQuery({
        queryKey: ['ai-changes', severityFilter, requiresActionOnly],
        queryFn: async () => {
            const params: Record<string, string> = { limit: '50' }
            if (severityFilter !== 'all') params.severity = severityFilter
            if (requiresActionOnly) params.requiresAction = 'true'
            const res = await api.get('/api/ai/models/changes', { params })
            return res.data
        },
        enabled: isAuthenticated,
    })

    const syncMutation = useMutation({
        mutationFn: () => api.post('/api/ai/models/sync'),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['ai-changes'] })
            queryClient.invalidateQueries({ queryKey: ['ai-changes-stats'] })
            queryClient.invalidateQueries({ queryKey: ['ai-sync-logs'] })
            queryClient.invalidateQueries({ queryKey: ['ai-models'] })
            queryClient.invalidateQueries({ queryKey: ['ai-model-preferences'] })
            toast({ title: t('aiModels.sync.completed', 'Sync completed') })
        },
        onError: () => {
            toast({ title: t('aiModels.sync.failed', 'Sync failed'), variant: 'destructive' })
        },
    })

    const stats = statsData || { total: 0, critical: 0, warning: 0, info: 0, thisWeek: 0, lastSync: null }
    const events: ChangeEvent[] = changesData?.events || []

    const lastSyncText = stats.lastSync?.startedAt
        ? t('aiModels.sync.lastSynced', 'Last synced {{when}}', { when: timeAgo(stats.lastSync.startedAt, t) })
        : t('aiModels.sync.neverSynced', 'Never synced')
    const summaryParts = [lastSyncText]
    if (stats.total > 0) summaryParts.push(t('aiModels.changes.countChanges', '{{n}} changes', { n: stats.total }))
    if (stats.critical > 0) summaryParts.push(t('aiModels.changes.countCritical', '{{n}} critical', { n: stats.critical }))

    const severityLabels: Record<SeverityFilter, string> = {
        all: t('common.all', 'All'),
        critical: t('aiModels.severity.critical', 'Critical'),
        warning: t('common.warning', 'Warning'),
        info: t('common.info', 'Info'),
    }

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-lg font-semibold">{t('aiModels.changes.title', 'Model Changes')}</h2>
                    <p className="text-sm text-muted-foreground">{summaryParts.join(' \u00b7 ')}</p>
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => syncMutation.mutate()}
                    disabled={syncMutation.isPending}
                >
                    <RefreshCw className={`mr-2 h-4 w-4 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
                    {t('aiModels.sync.syncNow', 'Sync Now')}
                </Button>
            </div>

            {/* Filter pills */}
            <div className="flex items-center gap-2 flex-wrap">
                {(['all', 'critical', 'warning', 'info'] as SeverityFilter[]).map((s) => (
                    <Button
                        key={s}
                        variant={severityFilter === s ? 'default' : 'outline'}
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => setSeverityFilter(s)}
                    >
                        {severityLabels[s]}
                    </Button>
                ))}
                <Button
                    variant={requiresActionOnly ? 'default' : 'outline'}
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => setRequiresActionOnly(!requiresActionOnly)}
                >
                    {t('aiModels.filters.requiresAction', 'Requires Action')}
                </Button>
            </div>

            {/* Change list */}
            {isLoading ? (
                <div className="text-sm text-muted-foreground py-8 text-center">{t('aiModels.changes.loading', 'Loading changes...')}</div>
            ) : events.length === 0 ? (
                <div className="text-sm text-muted-foreground py-8 text-center">
                    {t('aiModels.changes.empty', 'No model changes detected')}
                </div>
            ) : (
                <div className="space-y-1">
                    {events.map((event) => {
                        const badge = changeTypeBadge(event.changeType, t)
                        const isExpanded = expandedId === event.id
                        let prevValues: Record<string, any> | null = null
                        let newValues: Record<string, any> | null = null
                        let affectedScenarios: Array<{ scenarioKey: string; scenarioName: string; role: string }> = []
                        try { if (event.previousValue) prevValues = JSON.parse(event.previousValue) } catch {}
                        try { if (event.newValue) newValues = JSON.parse(event.newValue) } catch {}
                        try { if (event.affectedScenarios) affectedScenarios = JSON.parse(event.affectedScenarios) } catch {}

                        // Extract model display name from title
                        const modelName = event.title.replace(/^(New Model Available|Model Removed|Model Updated|Model Restored|Auto-assigned .* to): /, '')

                        return (
                            <div key={event.id}>
                                <div
                                    className="flex items-center gap-3 rounded-lg border p-3 cursor-pointer hover:bg-muted/30 transition-colors"
                                    onClick={() => setExpandedId(isExpanded ? null : event.id)}
                                >
                                    {/* Severity dot */}
                                    <span className={`h-2.5 w-2.5 rounded-full flex-shrink-0 ${severityDotColor(event.severity)}`} />

                                    {/* Change type badge */}
                                    <Badge variant="outline" className={`text-[10px] py-0 px-1.5 min-w-[70px] justify-center ${badge.className}`}>
                                        {badge.label}
                                    </Badge>

                                    {/* Model name + provider */}
                                    <div className="flex-1 min-w-0 flex items-center gap-2">
                                        <span className="text-sm font-medium">{modelName}</span>
                                        {event.provider && (
                                            <>
                                                {PROVIDER_ICONS[event.provider.name] ? (
                                                    <img src={PROVIDER_ICONS[event.provider.name]} alt={event.provider.displayName} className="h-4 w-4" title={event.provider.displayName} />
                                                ) : (
                                                    <span className="text-xs text-muted-foreground">{event.provider.displayName}</span>
                                                )}
                                            </>
                                        )}
                                    </div>

                                    {/* Capability icons for new models */}
                                    {event.changeType === 'new_model' && newValues && (
                                        <div className="flex items-center gap-1.5">
                                            {newValues.supportsImages && <ImageIcon className="h-3.5 w-3.5 text-muted-foreground" />}
                                            {newValues.supportsStreaming && <Zap className="h-3.5 w-3.5 text-muted-foreground" />}
                                        </div>
                                    )}

                                    {/* Time + expand */}
                                    <span className="text-xs text-muted-foreground whitespace-nowrap">{timeAgo(event.createdAt, t)}</span>
                                    <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                </div>

                                {/* Expanded details */}
                                {isExpanded && (
                                    <div className="ml-6 mr-3 mb-2 p-3 rounded-b-lg border border-t-0 bg-muted/20 space-y-2">
                                        <p className="text-sm text-muted-foreground">{event.description}</p>

                                        {/* Diff for updates */}
                                        {event.changeType === 'model_updated' && prevValues && newValues && (
                                            <div className="bg-muted/50 rounded p-2 space-y-0.5">
                                                {Object.keys(newValues).map((key) => (
                                                    <div key={key} className="text-xs font-mono">
                                                        <span className="text-muted-foreground">{key}:</span>{' '}
                                                        <span className="text-red-500 line-through">{String(prevValues?.[key] ?? 'null')}</span>
                                                        {' \u2192 '}
                                                        <span className="text-green-500">{String(newValues[key] ?? 'null')}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {/* Capabilities for new models */}
                                        {event.changeType === 'new_model' && newValues && (
                                            <div className="flex gap-2 flex-wrap">
                                                {newValues.contextWindow && (
                                                    <Badge variant="outline" className="text-xs">{t('aiModels.capabilities.context', 'Context: {{size}}K', { size: (newValues.contextWindow / 1000).toFixed(0) })}</Badge>
                                                )}
                                                {newValues.maxTokens && (
                                                    <Badge variant="outline" className="text-xs">{t('aiModels.capabilities.maxOutput', 'Max Output: {{size}}K', { size: (newValues.maxTokens / 1000).toFixed(0) })}</Badge>
                                                )}
                                            </div>
                                        )}

                                        {/* Affected scenarios */}
                                        {affectedScenarios.length > 0 && (
                                            <div className="space-y-1">
                                                <div className="text-xs text-yellow-600 dark:text-yellow-400 font-medium">
                                                    {affectedScenarios.length > 1
                                                        ? t('aiModels.changes.affectsScenarios', 'Affects {{n}} scenarios:', { n: affectedScenarios.length })
                                                        : t('aiModels.changes.affectsScenario', 'Affects {{n}} scenario:', { n: affectedScenarios.length })}
                                                </div>
                                                {affectedScenarios.map((s) => (
                                                    <div key={s.scenarioKey} className="flex items-center gap-2 text-xs ml-2">
                                                        <span>{s.scenarioName}</span>
                                                        <Badge variant={s.role === 'PRIMARY' ? 'destructive' : 'secondary'} className="text-[9px] py-0">{s.role}</Badge>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
            )}
        </div>
    )
}
